/**
 * Prism Unpoly 组件
 *
 * 职责：
 * 1. 懒加载 prism.js（IntersectionObserver 触发）
 * 2. 初始化语法高亮和代码块交互（复制按钮）
 * 3. 处理暗色/亮色主题切换
 */

import { loadScript, loadStyle } from "@/utils/loader";

/** 全局单例：确保 Prism 只加载一次（SPA 导航场景） */
let prismLoadStatus: "unloaded" | "loading" | "loaded" = "unloaded";

/** CDN 资源配置 */
const PRISM_CONFIG = {
  urls: {
    core: "https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/prism.min.js",
    autoloader:
      "https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/plugins/autoloader/prism-autoloader.min.js",
    componentsPath:
      "https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/components/",
    darkTheme:
      "https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/prism-tomorrow.min.css",
    lightTheme:
      "https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/prism.min.css",
  },
  icons: {
    copy: `<svg class="w-4 h-4"><use href="/public/icons.svg#icon-copy"></use></svg>`,
    check: `<svg class="w-4 h-4"><use href="/public/icons.svg#icon-check"></use></svg>`,
  },
} as const;

export class Prism {
  constructor(private element: HTMLElement) {
    this.init();
  }

  private init(): void {
    // Prism 已加载过：直接高亮，无需懒加载
    if (prismLoadStatus === "loaded") {
      this.highlight();
      return;
    }

    // 1. 监听内容进入视口事件
    up.on(this.element, "article:content:visible", this.load);

    // 3. 主题切换监听
    up.on("theme:changed", this.updateTheme);
  }

  /**
   * 加载 Prism 核心脚本、主题样式和 autoloader 插件
   */
  private load = async () => {
    if (prismLoadStatus !== "unloaded") return;

    // 1. 检查是否存在代码块，避免无谓加载
    if (!this.element.querySelector("pre, code")) {
      console.log(
        "[Prism] No code blocks found. Skipping PrismJS resource loading.",
      );
      return;
    }

    console.log("[Prism] Starting PrismJS resource loading...");
    prismLoadStatus = "loading";

    try {
      await Promise.all([
        loadScript(PRISM_CONFIG.urls.core),
        loadStyle(PRISM_CONFIG.urls.darkTheme, "prism-dark"),
        loadStyle(PRISM_CONFIG.urls.lightTheme, "prism-light"),
      ]);

      console.log("[Prism] Prism Core & CSS loaded. Applying initial theme...");
      this.updateTheme();

      console.log("[Prism] Loading autoloader...");
      await loadScript(PRISM_CONFIG.urls.autoloader);

      // 配置 autoloader 组件路径
      if (window.Prism?.plugins?.autoloader) {
        window.Prism.plugins.autoloader.languages_path =
          PRISM_CONFIG.urls.componentsPath;
        console.log(
          "[Prism] Prism Autoloader configured with components path.",
        );
      }

      prismLoadStatus = "loaded";
      this.highlight();
      console.log("[Prism] PrismJS enhancement complete.");
    } catch (err) {
      console.error("[Prism] Failed to load PrismJS resources:", err);
      prismLoadStatus = "unloaded"; // 允许重试
    }
  };

  /**
   * 根据当前主题切换 Prism 样式表
   */
  private updateTheme = () => {
    if (prismLoadStatus !== "loaded") return;

    const localTheme = localStorage.getItem("theme");
    const hasDarkClass = document.documentElement.classList.contains("dark");
    const isDark =
      localTheme === "dark" || (hasDarkClass && localTheme !== "light");

    const darkLink = document.getElementById(
      "prism-dark",
    ) as HTMLLinkElement | null;
    const lightLink = document.getElementById(
      "prism-light",
    ) as HTMLLinkElement | null;

    console.log(`[Prism] Applying Prism theme. isDark: ${isDark}`);
    if (darkLink && lightLink) {
      darkLink.disabled = !isDark;
      lightLink.disabled = isDark;
    }
  };

  /**
   * 对组件根元素内的代码块执行语法高亮并添加复制按钮
   */
  highlight(): void {
    if (typeof window.Prism === "undefined") return;

    // 语法高亮
    window.Prism.highlightAllUnder(this.element);

    // 同步主题
    this.updateTheme();

    // 为 <pre> 添加复制按钮
    this.element.querySelectorAll("pre").forEach((pre: HTMLElement) => {
      if (!pre.dataset.enhanced) {
        const code = pre.querySelector("code");
        if (code) {
          this.addCopyButton(pre, code);
          pre.dataset.enhanced = "true";
        }
      }
    });
  }

  /**
   * 为代码块添加复制按钮
   */
  private addCopyButton(pre: HTMLElement, code: HTMLElement): void {
    // 创建包裹容器（overflow-hidden + min-w-0 防止 pre 内容撑破外层）
    const wrapper = document.createElement("div");
    wrapper.className =
      "relative group/code my-[2.5rem] overflow-hidden min-w-0";
    pre.style.margin = "0"; // 避免双重 margin
    pre.parentNode?.insertBefore(wrapper, pre);
    wrapper.appendChild(pre);

    const button = document.createElement("button");
    button.className =
      "copy-button absolute top-3 right-3 p-2 rounded-lg bg-surface/50 backdrop-blur-md border border-border opacity-0 transition-all hover:bg-primary hover:text-white focus:ring-2 focus:ring-primary z-10 group-hover/code:opacity-100";
    button.innerHTML = PRISM_CONFIG.icons.copy;
    button.title = "Copy code";

    button.onclick = () => {
      const text = code.textContent || "";
      navigator.clipboard.writeText(text).then(() => {
        button.innerHTML = PRISM_CONFIG.icons.check;
        setTimeout(() => {
          button.innerHTML = PRISM_CONFIG.icons.copy;
        }, 2000);
      });
    };

    wrapper.appendChild(button);
  }

  destroy(): void {
    up.off("article:content:visible", this.load);
    up.off("theme:changed", this.updateTheme);
  }
}
